import React, { useEffect, useRef, useState } from 'react';
import { Alert, Box, Grid, IconButton, Typography } from '@mui/material';
import { Delete, Download, UploadFile } from '@mui/icons-material';
import { IPersonHasDocument } from '../../../../Interfaces/IPersonHasDocument';
import { useSelector } from 'react-redux';
import { State } from '../../../../redux/mainReducer';
import { IDocument } from '../../../../Interfaces/IDocument';
import { getFetch } from '../../../../hooks/useFetch';

export interface IRequiredFiles {
  Title: string;
  isOptional: boolean;
  idPersonDocumentType: number;
}

interface IProps {
  requiredFile: IRequiredFiles;
  idPerson: number;
  personHasDocument: IPersonHasDocument[];
  setPersonHasDocument: Function;
  disableEdit?: boolean;
}

const getDocumentObject = (
  idPerson: number,
  requiredFile: IRequiredFiles,
  personHasDocument: IPersonHasDocument[]
) => {
  const testObject = personHasDocument
    .filter(x => x.idPersonDocumentType === requiredFile.idPersonDocumentType)
    .find(x => x.idPerson === idPerson);

  if (testObject !== undefined) {
    return testObject;
  } else {
    let minId = Math.min(...personHasDocument.map(x => x.idDocument)) - 1;

    if (minId > 0) {
      minId = -1;
    }

    return {
      FileName: '',
      idDocument: minId,
      idMIMEType: 1,
      idPerson: idPerson,
      idPersonDocumentType: requiredFile.idPersonDocumentType,
    } as IPersonHasDocument;
  }
};

export const InheritDocument: React.FC<IProps> = ({
  requiredFile,
  idPerson,
  personHasDocument,
  setPersonHasDocument,
  disableEdit,
}) => {
  const mimeTypeArray = useSelector(
    (state: State) => state.mimeTypeArray.storeAcademicTitle
  );

  const [currentObject, setCurrentObject] = useState(
    getDocumentObject(idPerson, requiredFile, personHasDocument)
  );
  const fileUpload = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const documentObject = getDocumentObject(
      idPerson,
      requiredFile,
      personHasDocument
    );

    setCurrentObject(documentObject);
  }, [personHasDocument]);

  const [documentObject, setDocumentObject] = useState<IDocument | null>(null);

  const base64Download = (
    localDocumentObject: IPersonHasDocument | IDocument
  ) => {
    console.log(localDocumentObject.Data);
    const findMimeType = mimeTypeArray.find(
      x => x.idMIMEType === localDocumentObject.idMIMEType
    );

    if (findMimeType !== undefined && localDocumentObject.Data !== undefined) {
      const byteCharacters = atob(localDocumentObject.Data);
      // Each character's code point (charCode) will be the value of the byte.
      // We can create an array of byte values by applying this using the .charCodeAt method for each character in the string.
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      //You can convert this array of byte values into a real typed byte array by passing it to the Uint8Array constructor.
      const byteArray = new Uint8Array(byteNumbers);
      // This in turn can be converted to a BLOB by wrapping it in an array and passing it to the Blob constructor.
      const blob = new Blob([byteArray], { type: findMimeType.MIMEType });
      // Erstelle Link zum BLOB
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = localDocumentObject.FileName;
      a.click();
    }
  };

  async function fileToBase64(file: File) {
    const result_base64 = await new Promise(resolve => {
      const fileReader = new FileReader();
      fileReader.onload = e => resolve(fileReader.result);
      fileReader.readAsDataURL(file);
    });
    return String(result_base64).split(',')[1];
  }

  const saveDocument = async (file: File) => {
    const findMimeType = mimeTypeArray.find(x => x.MIMEType === file.type);

    if (findMimeType !== undefined) {
      const tmpObject: IPersonHasDocument = {
        ...currentObject,
        FileName: String(file.name),
        idMIMEType: findMimeType.idMIMEType,
        Data: await fileToBase64(file),
      };

      setCurrentObject({ ...tmpObject });

      const findObject = personHasDocument.find(
        x => x.idDocument === tmpObject.idDocument
      );

      if (findObject === undefined) {
        setPersonHasDocument([...personHasDocument, tmpObject]);
      } else {
        setPersonHasDocument([
          ...personHasDocument.map(x =>
            x.idDocument === tmpObject.idDocument ? tmpObject : x
          ),
        ]);
      }
    }
  };

  const handleUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files?.length === 1) {
      saveDocument(event.target.files[0]);
    }
  };

  const handleRemove = () => {
    setCurrentObject({ ...currentObject, Data: undefined });
    setPersonHasDocument([
      ...personHasDocument.filter(
        x =>
          x.idPerson !== idPerson &&
          x.idPersonDocumentType === requiredFile.idPersonDocumentType
      ),
    ]);
  };

  const wrapperSetDocumentObject = (localDocument: IDocument) => {
    setDocumentObject(localDocument);
    base64Download(localDocument);
  };

  const handleDownload = () => {
    getFetch('/document/', currentObject.idDocument, wrapperSetDocumentObject);
  };

  return (
    <>
      <input
        hidden
        type='file'
        ref={fileUpload}
        accept={String(mimeTypeArray.map(x => x.MIMEType).join(', '))}
        onChange={handleUpload}
      />

      <Grid item sm={12}>
        <Alert
          severity={
            currentObject.Data !== undefined || currentObject.idDocument > 0
              ? 'success'
              : requiredFile.isOptional
                ? 'info'
                : 'warning'
          }
          action={
            currentObject.Data === undefined && currentObject.idDocument < 0 ? (
              <Box sx={{ mt: 0.5 }}>
                <IconButton onClick={() => fileUpload.current?.click()}>
                  <UploadFile />
                </IconButton>
              </Box>
            ) : (
              <Box sx={{ mt: 0.5 }}>
                <IconButton onClick={handleDownload}>
                  <Download />
                </IconButton>
                {(disableEdit === undefined || disableEdit === false) && (
                  <IconButton onClick={handleRemove}>
                    <Delete />
                  </IconButton>
                )}
              </Box>
            )
          }
        >
          {requiredFile.Title}
          <br />
          <Typography variant='caption'>
            {requiredFile.isOptional ? '(optional)' : '(notwendiges Dokument)'}
          </Typography>
        </Alert>
      </Grid>
    </>
  );
};
